/**
 * Pipeline API Service
 *
 * Handles pipeline analysis, tracing, and performance monitoring
 */

import { apiClient } from './client';
import {
  APIResponse,
  MultiAgentStatusResponse,
  WorkflowExecutionResponse,
  MultiAgentAnalysisRequest,
  MultiAgentTraceResponse
} from '../../types';

class PipelineAPI {
  // Pipeline Analysis Methods for Task 48
  async startPipelineAnalysis(projectId: string, request: MultiAgentAnalysisRequest): Promise<APIResponse> {
    try {
      const response = await apiClient.post(`/api/v1/projects/${projectId}/analyze/pipeline`, request);
      return response.data;
    } catch (error: any) {
      // Plan 145 phase 3 (#679): admission control returns 413 with a specific
      // detail string. Surface that exact message so users can see whether
      // they're over the per-doc, aggregate, or document-count cap.
      if (error.response?.status === 413 && error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  async getStageStatus(projectId: string): Promise<MultiAgentStatusResponse> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/analysis/stages`);
    return response.data;
  }

  async getWorkflowExecution(projectId: string): Promise<WorkflowExecutionResponse> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/workflow-execution`);
    return response.data;
  }

  async getAnalysisTrace(projectId: string): Promise<MultiAgentTraceResponse> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/analysis/trace`);
    return response.data;
  }

  // Enhanced analysis status with LangSmith integration (Task 62 enhancement)
  async getEnhancedAnalysisStatus(projectId: string): Promise<APIResponse> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/analysis/enhanced-status`);
    return response.data;
  }

  async getWorkflowPerformanceMetrics(projectId: string): Promise<APIResponse> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/workflow-performance`);
    return response.data;
  }

  async getWorkflowCostAnalysis(projectId: string): Promise<APIResponse> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/workflow-costs`);
    return response.data;
  }

  async getFallbackStatus(projectId: string): Promise<APIResponse> {
    const response = await apiClient.get(`/api/v1/projects/${projectId}/fallback-status`);
    return response.data;
  }

  async cancelWorkflow(analysisRunId: string): Promise<APIResponse> {
    const response = await apiClient.post(`/api/v1/analysis-runs/${analysisRunId}/cancel`);
    return response.data;
  }
}

export const pipelineApi = new PipelineAPI();
// Backward compatibility alias
export const multiAgentApi = pipelineApi;
